<script lang="ts" setup>
import { computed, onMounted } from 'vue';
import { i18n } from '@/i18n';
import { SelectYearMonthPattern, UploadInputFileType } from '../../interfaces/UploadFile';
import SelectableField from '../SelectableField.vue';
import { assertNever } from '@/coreDomainsModule/assertNever';
import {
  AlterableHeader,
  AlterableHeaderInfo,
  AlterableHeaderService,
  isRequireAlterableHeader,
  SelectYearMonthPatternInHeader,
  SelectYearMonthPatternInlined,
} from '../../interfaces/LinkingInput';
import InputExcelYearMonthInfo from './InputExcelYearMonthInfo.vue';

const t = i18n.t;

/**
 * 呼び出し元
 */
export type From = 'LinkingInput' | 'HomeInput' | 'FinishInput';

const props = withDefaults(
  defineProps<{
    from: From;
    selectedFieldId: string;
    suggestions: string[];
    inputFileType: UploadInputFileType;
    useFormatType: SelectYearMonthPattern;
    alterableHeaderInfos: AlterableHeaderInfo[];
    inlinedYearMonthInfo: SelectYearMonthPatternInlined;
    inHeaderYearMonthInfo: SelectYearMonthPatternInHeader;
  }>(),
  {}
);

const emits = defineEmits<{
  (e: 'update-selected-field', header: AlterableHeaderInfo, updateValue: string): void;
  (e: 'update-selected-field-id', selectedFieldId: string): void;
  (e: 'update-inline-header-name', updateValue: string): void;
  (e: 'update-in-header-start-column-number', updateValue: string): void;
  (e: 'update-in-header-end-column-number', updateValue: string): void;
  (e: 'update-in-header-start', updateValue: string): void;
  (e: 'update-in-header-end', updateValue: string): void;
}>();

/**
 * 選択されたフィールドを更新する
 * @param header - フィールド
 * @param updateValue - 更新する値
 */
const updateSelectedField = (header: AlterableHeaderInfo, updateValue: string) => {
  emits('update-selected-field', header, updateValue);
};

/**
 * 選択されたフィールドIDを更新する
 * @param id - フィールドID
 */
const updateSelectedFieldId = (id: string) => {
  emits('update-selected-field-id', id);
};

/**
 * 読み替え項目のヘッダー表示
 * @param header - ヘッダー
 */
const displayHeaderTitle = (header: AlterableHeader): string => {
  switch (header) {
    case 'WarehouseNumber':
      return addRequiredMark('WarehouseNumber', t('置場コード'));
    case 'Warehouse':
      return addRequiredMark('Warehouse', t('置場名'));
    case 'SupplierNumber':
      return addRequiredMark('SupplierNumber', t('仕入先コード'));
    case 'SupplierName':
      return addRequiredMark('SupplierName', t('仕入先名'));
    case 'BusinessNumber':
      return addRequiredMark('BusinessNumber', t('商品コード'));
    case 'BusinessName':
      return addRequiredMark('BusinessName', t('商品名'));
    case 'Package':
      return addRequiredMark('Package', t('容量'));
    case 'CustomerNumber':
      return addRequiredMark('CustomerNumber', t('売上先コード'));
    case 'CustomerName':
      return addRequiredMark('CustomerName', t('売上先名'));
    case 'SalesDestinationNumber':
      return addRequiredMark('SalesDestinationNumber', t('送り先コード'));
    case 'SalesDestinationName':
      return addRequiredMark('SalesDestinationName', t('送り先名'));
    case 'InventoryValue':
      return addRequiredMark('InventoryValue', t('在庫数量'));
    case 'SalesResultsValue':
      return addRequiredMark('SalesResultsValue', t('販売実績数量'));
    case 'ArrivalValue':
      return addRequiredMark('ArrivalValue', t('入荷実績数量'));
    case 'Manufacturer':
      return addRequiredMark('Manufacturer', t('製造社'));
    case 'EndUser':
      return addRequiredMark('EndUser', t('エンドユーザー'));
  }
};

/**
 * 読み替え項目のヘッダー表示（必須マーク付き）
 * @param header - ヘッダー名
 * @param value - ヘッダー表示
 * @returns - ヘッダー表示
 */
const addRequiredMark = (header: AlterableHeader, value: string): string => {
  return isRequireAlterableHeader(header) ? `${value} *` : value;
};

const headerList = computed(() => {
  switch (props.inputFileType) {
    case '在庫':
      return AlterableHeaderService.inventoryAlterableHeaderList;
    case '販売実績':
      return AlterableHeaderService.salesResultsAlterableHeaderList;
    case '入荷':
      return AlterableHeaderService.arrivalAlterableHeaderList;
    default:
      assertNever(props.inputFileType);
      return [];
  }
});

/**
 * getter
 * @param header - ヘッダー
 */
const unusedLabel = (header: AlterableHeaderInfo) => {
  if (header.header === 'Package' && header.unused) {
    return header.unused ? t('取引紐付け：文言不使用（数値）') : '';
  } else if (header.unused) {
    return header.unused ? t('取引紐付け：文言不使用') : '';
  } else {
    return '';
  }
};
</script>

<template>
  <div class="linking-input__linking-container linking-container">
    <div class="linking-container__left">
      <template v-for="header in alterableHeaderInfos" :key="`linking-input-header-${header.key}`">
        <div class="linking-container__at">
          <div class="linking-container__at--content">
            <div class="linking-container__at--label label-container">
              <span class="linking-container__at--label">{{ displayHeaderTitle(header.header) }}</span>
              <template v-if="header.unused != null && from !== 'HomeInput' && isRequireAlterableHeader(header.header)">
                <input v-model="header.unused" type="checkbox" class="label-container__checkbox" />
                <span class="label-container__unused-label">{{ t('取引紐付け：チェックボックス：不使用') }}</span>
              </template>
            </div>
            <template v-if="!header.unused">
              <SelectableField
                :value="header.value"
                :selected-id="selectedFieldId"
                :hint="t('取引紐付け：サジェストヒント')"
                :suggestions="suggestions"
                @update-value="updateSelectedField(header, $event)"
                @update-selected-id="updateSelectedFieldId"
              />
            </template>
            <template v-else>
              <span class="linking-container__at--unused">{{ unusedLabel(header) }}</span>
            </template>
          </div>
          <span class="linking-container__at--error">{{ header.validateError ? t('取引紐付け：アラート文言：必須項目') : '　' }}</span>
        </div>
      </template>
    </div>

    <div class="linking-container__separator" />

    <div class="linking-container__right">
      <InputExcelYearMonthInfo
        :select-year-month-pattern="useFormatType"
        :inlined-year-month-info="inlinedYearMonthInfo"
        :in-header-year-month-info="inHeaderYearMonthInfo"
        @update-inline-header-name="($event) => emits('update-inline-header-name', $event)"
        @update-in-header-start-column-number="($event) => emits('update-in-header-start-column-number', $event)"
        @update-in-header-end-column-number="($event) => emits('update-in-header-end-column-number', $event)"
        @update-in-header-start="($event) => emits('update-in-header-start', $event)"
        @update-in-header-end="($event) => emits('update-in-header-end', $event)"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.linking-container {
  display: grid;
  gap: 16px;
  grid-template-columns: 1fr min-content fit-content(100%);

  &__left {
    display: grid;
    margin-top: 16px;
    gap: 32px;
    grid-template-columns: repeat(auto-fit, minmax(100px, 300px));
    grid-auto-rows: max-content;
  }

  &__at {
    display: grid;
    grid-template-rows: repeat(2, fit-content(100%));

    &--content {
      display: grid;
      grid-template-rows: repeat(2, fit-content(100%));
      border-bottom: 1px solid $borderDefault;
    }

    &--label {
      margin-bottom: 8px;
      font-family: Noto Sans JP;
      font-size: $fontSizeS;
      font-weight: 400;
      color: $textDefault;
    }

    &--error {
      font-family: Noto Sans JP;
      font-size: $fontSizeS;
      font-weight: 400;
      color: $semanticError;
    }

    &--unused {
      font-family: Noto Sans JP;
      font-size: $fontSizeS;
      font-weight: 400;
      color: $semanticWarning;
    }
  }

  &__separator {
    background-color: $borderDefault;
    width: 1px;
    margin: 0px 72px;
  }
}

.label-container {
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  background-color: $semanticWhite;

  &__title {
    font-family: Noto Sans JP;
    font-size: $fontSizeS;
    font-weight: 400;
    color: $textDefault;
  }

  &__checkbox {
    height: 16px;
    width: 16px;
  }

  &__unused-label {
    margin-left: 4px;
    padding-bottom: 4px;
    font-family: Noto Sans JP;
    font-size: $fontSizeSS;
    font-weight: 400;
    color: $textDefault;
  }
}
</style>
